// Shared dashboard control bar: preset time range OR a custom date range.
// Controlled component — parent owns state and refetches on change.

import { useEffect, useRef, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";

export const RANGE_OPTIONS = [
  { label: "3 days", days: 3 },
  { label: "5 days", days: 5 },
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
  { label: "1 month", days: 30 },
  { label: "3 months", days: 90 },
  { label: "1 year", days: 365 },
];

const inputClass =
  "rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-sm text-gray-800 dark:text-gray-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-400";

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
// Spelled out rather than taken from date-fns/locale, whose ESM-only entry
// the react-scripts Jest can't load (same as components/usageBilling/usageFormat.js).
const DAY_NAMES_ZH = ["日", "一", "二", "三", "四", "五", "六"];

// ─── Single month grid ───────────────────────────────────────────────────────
function MonthGrid({
  month,
  startDate,
  endDate,
  hovered,
  onDayClick,
  onDayEnter,
  onDayLeave,
  language = "en",
}) {
  const monthStart = startOfMonth(month);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });
  const weeks = [];
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7));

  const start = startDate ? parseISO(startDate) : null;
  const end = endDate ? parseISO(endDate) : null;
  const todayStart = startOfDay(new Date());

  const isInRange = (day) => {
    if (!start) return false;
    const effectiveEnd = !end && hovered ? hovered : end;
    if (!effectiveEnd) return isSameDay(day, start);
    const [s, e] = isBefore(start, effectiveEnd)
      ? [start, effectiveEnd]
      : [effectiveEnd, start];
    try {
      return isWithinInterval(day, { start: s, end: e });
    } catch {
      return false;
    }
  };

  return (
    <div className="w-44">
      <p className="text-center text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
        {format(month, language === "zh" ? "yyyy年M月" : "MMMM yyyy")}
      </p>
      <div className="grid grid-cols-7 text-center text-[10px] text-gray-400 mb-1">
        {(language === "zh" ? DAY_NAMES_ZH : DAY_NAMES).map((d) => (
          <span key={d} className="py-0.5">
            {d}
          </span>
        ))}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} className="grid grid-cols-7">
          {week.map((day, di) => {
            const inMonth = isSameMonth(day, month);
            const isFuture = isAfter(day, todayStart);
            const selectable = inMonth && !isFuture;
            const isStart = start && isSameDay(day, start);
            const isEnd = end && isSameDay(day, end);
            const inRange = isInRange(day);

            return (
              <button
                key={di}
                type="button"
                disabled={!selectable}
                onClick={() => selectable && onDayClick(day)}
                onMouseEnter={() => selectable && onDayEnter(day)}
                onMouseLeave={onDayLeave}
                className={[
                  "text-[11px] py-1 leading-none transition-colors",
                  !inMonth
                    ? "invisible"
                    : isFuture
                      ? "text-gray-300 dark:text-gray-600 cursor-not-allowed"
                      : isStart || isEnd
                        ? "bg-orange-500 text-white font-bold rounded-full cursor-pointer"
                        : inRange
                          ? "bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 cursor-pointer"
                          : "text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer",
                ].join(" ")}
              >
                {format(day, "d")}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// ─── 2-month calendar (flight-ticket style range selection) ──────────────────
// Also used by the trend reports date filter (TrendReports/tabs/ReportDateFilter.js).
export function Calendar({ startDate, endDate, onChange, language = "en" }) {
  const today = new Date();
  const [leftMonth, setLeftMonth] = useState(
    startDate ? subMonths(parseISO(startDate), 1) : subMonths(today, 1),
  );
  const [hovered, setHovered] = useState(null);

  const rightMonth = addMonths(leftMonth, 1);
  // Don't let the right month advance beyond the current month.
  const atLatest = !isBefore(startOfMonth(rightMonth), startOfMonth(today));

  const handleDayClick = (day) => {
    const start = startDate ? parseISO(startDate) : null;
    // Second click on a later day completes the range; otherwise start over.
    if (start && !endDate && !isBefore(day, start) && !isSameDay(day, start)) {
      onChange({ startDate, endDate: format(day, "yyyy-MM-dd") });
    } else {
      onChange({ startDate: format(day, "yyyy-MM-dd"), endDate: "" });
    }
  };

  const sharedProps = {
    startDate,
    endDate,
    hovered,
    onDayClick: handleDayClick,
    onDayEnter: (day) => !endDate && setHovered(day),
    onDayLeave: () => setHovered(null),
    language,
  };

  return (
    <div className="flex gap-6 items-start relative">
      {/* Prev / next arrows anchored to the two-month block */}
      <button
        type="button"
        onClick={() => setLeftMonth((m) => subMonths(m, 1))}
        className="absolute -left-1 top-0 px-1.5 py-0.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 text-base leading-none"
      >
        ‹
      </button>
      <button
        type="button"
        disabled={atLatest}
        onClick={() => !atLatest && setLeftMonth((m) => addMonths(m, 1))}
        className="absolute -right-1 top-0 px-1.5 py-0.5 rounded text-base leading-none text-gray-500 enabled:hover:bg-gray-100 dark:enabled:hover:bg-gray-700 disabled:text-gray-300 dark:disabled:text-gray-600 disabled:cursor-not-allowed"
      >
        ›
      </button>
      <MonthGrid month={leftMonth} {...sharedProps} />
      <MonthGrid month={rightMonth} {...sharedProps} />
    </div>
  );
}

// ─── Control bar ─────────────────────────────────────────────────────────────
function DashboardControls({
  rangeDays,
  setRangeDays,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const custom = Boolean(startDate && endDate);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  // Label shown on the trigger button.
  const triggerLabel = custom
    ? `${format(parseISO(startDate), "MMM d, yyyy")} → ${format(parseISO(endDate), "MMM d, yyyy")}`
    : startDate
      ? `${format(parseISO(startDate), "MMM d, yyyy")} → …`
      : RANGE_OPTIONS.find((o) => o.days === rangeDays)?.label ||
        "Select range";

  const selectPreset = (days) => {
    setStartDate("");
    setEndDate("");
    setRangeDays(days);
    setOpen(false);
  };

  const handleCalendarChange = ({ startDate: s, endDate: e }) => {
    setStartDate(s);
    setEndDate(e);
    if (s && e) setOpen(false); // auto-close once a full range is picked
  };

  return (
    <div className="flex flex-wrap w-full justify-end items-center gap-4 mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm px-4 py-3">
      <div
        className="relative flex items-center gap-2 text-xs font-medium text-gray-500 dark:text-gray-400"
        ref={rootRef}
      >
        Time range
        {/* Single trigger */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={`${inputClass} flex items-center gap-2`}
        >
          {loading ? (
            <svg
              className="w-3.5 h-3.5 text-orange-500 animate-spin"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
          ) : (
            <svg
              className="w-3.5 h-3.5 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          )}
          {triggerLabel}
          <svg
            className={`w-3 h-3 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {/* Two-pane popover: calendar (left) + preset list (right) */}
        {open && (
          <div className="absolute z-50 top-full mt-2 right-0 flex bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl">
            {/* Left pane — flight-ticket calendar */}
            <div className="p-4">
              <Calendar
                startDate={startDate}
                endDate={endDate}
                onChange={handleCalendarChange}
              />
              <div className="mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>
                  {startDate
                    ? endDate
                      ? `${format(parseISO(startDate), "MMM d, yyyy")}  →  ${format(parseISO(endDate), "MMM d, yyyy")}`
                      : `${format(parseISO(startDate), "MMM d, yyyy")}  →  pick end date`
                    : "Click a start and end date"}
                </span>
                {(startDate || endDate) && (
                  <button
                    type="button"
                    onClick={() => {
                      setStartDate("");
                      setEndDate("");
                    }}
                    className="text-orange-500 hover:text-orange-600 ml-4"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>

            {/* Right pane — preset ranges */}
            <div className="w-36 shrink-0 border-l border-gray-100 dark:border-gray-700 p-2 flex flex-col gap-0.5">
              <p className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                Quick ranges
              </p>
              {RANGE_OPTIONS.map((o) => {
                const active = !custom && !startDate && rangeDays === o.days;
                return (
                  <button
                    key={o.days}
                    type="button"
                    onClick={() => selectPreset(o.days)}
                    className={`text-left text-xs px-2 py-1.5 rounded-md transition-colors ${
                      active
                        ? "bg-orange-500 text-white font-semibold"
                        : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    }`}
                  >
                    Last {o.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DashboardControls;
